<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" @click="handleCreateCampaign" :disabled="loading">
          <i class="ri-add-line"></i>
          新增註冊禮活動
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="isWixtarClient" class="wixtar-gift-notice">
        <i class="ri-information-line"></i>
        <div>
          <p class="wixtar-gift-notice-title">Wixtar 會員模式：註冊禮分工</p>
          <p class="wixtar-switch-status">
            會員事件觸發註冊禮：
            <span
              class="badge"
              :class="wixtarWebhookRegistrationGiftEnabled ? 'badge-success' : 'badge-secondary'"
            >
              {{ wixtarWebhookRegistrationGiftEnabled ? '已啟用' : '已停用' }}
            </span>
            <router-link to="/client/settings?tab=wixtar" class="wixtar-switch-link">前往設定</router-link>
          </p>
          <ul>
            <li>開啟後，由會員新建統一發禮（含 Wixtar webhook、LIFF 寫入 Wixtar 後的首次建檔）。</li>
            <li>Wixtar 自有註冊禮或複雜行銷流程時，請關閉該開關並停用本頁活動，避免重複。</li>
          </ul>
        </div>
      </div>

      <div class="page-toolbar">
        <div class="filter-group">
          <input
            v-model="searchQuery"
            type="text"
            class="form-control"
            placeholder="搜尋活動名稱..."
            @input="handleSearch"
          />
        </div>
        <div class="filter-group">
          <select v-model="filterStatus" class="form-control" @change="handleSearch">
            <option value="">全部狀態</option>
            <option value="active">生效中</option>
            <option value="disabled">已停用</option>
            <option value="scheduled">尚未生效</option>
            <option value="expired">已過期</option>
          </select>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>活動名稱</th>
              <th>生效起</th>
              <th>生效迄</th>
              <th>開關</th>
              <th>狀態</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filteredCampaigns.length === 0">
              <td colspan="6" class="empty-state">尚無註冊禮活動</td>
            </tr>
            <tr v-for="campaign in filteredCampaigns" :key="campaign.id">
              <td>{{ campaign.moduleName }}</td>
              <td>{{ formatDate(campaign.activeStartAt) }}</td>
              <td>{{ campaign.activeEndAt ? formatDate(campaign.activeEndAt) : '無限制' }}</td>
              <td>
                <span class="badge" :class="campaign.isEnabled ? 'badge-success' : 'badge-secondary'">
                  {{ campaign.isEnabled ? '已啟用' : '已停用' }}
                </span>
              </td>
              <td>
                <span class="badge" :class="statusBadgeClass(campaign.effectiveStatus)">
                  {{ statusLabel(campaign.effectiveStatus) }}
                </span>
              </td>
              <td>
                <div class="action-buttons">
                  <router-link
                    :to="{ name: 'RegistrationGiftConfigEdit', params: { moduleId: campaign.id } }"
                    class="btn btn-sm btn-primary"
                  >
                    <i class="ri-edit-line"></i>
                    編輯
                  </router-link>
                  <button
                    type="button"
                    class="btn btn-sm btn-danger"
                    :disabled="campaigns.length <= 1"
                    @click="handleDeleteCampaign(campaign)"
                  >
                    <i class="ri-delete-bin-line"></i>
                    刪除
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="createDialogOpen"
        class="create-lightbox-overlay"
        role="dialog"
        aria-modal="true"
        @click.self="closeCreateDialog"
      >
        <div class="create-lightbox">
          <h3 class="create-lightbox-title">新增註冊禮活動</h3>
          <p class="create-lightbox-text">請輸入活動名稱，建立後將複製第一個活動的設定作為初始內容。</p>
          <div class="form-group">
            <label class="form-label" for="create-campaign-name">活動名稱</label>
            <input
              id="create-campaign-name"
              ref="createNameInput"
              v-model="newCampaignName"
              type="text"
              class="form-control"
              maxlength="100"
              placeholder="例如：2026 新會員註冊禮"
              :disabled="createSubmitting"
              @keydown.enter.prevent="confirmCreateCampaign"
            />
            <span v-if="createError" class="error-text">{{ createError }}</span>
          </div>
          <div class="create-lightbox-actions">
            <button type="button" class="btn btn-secondary" :disabled="createSubmitting" @click="closeCreateDialog">
              取消
            </button>
            <button type="button" class="btn btn-primary" :disabled="createSubmitting" @click="confirmCreateCampaign">
              <span v-if="createSubmitting"><i class="ri-loader-4-line spin"></i> 建立中...</span>
              <span v-else>建立</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { autoSendService } from '@/api/modules/autoSend.js';
import { clientService } from '@/api/modules/clients.js';
import { clientSettingsService, NAMESPACES } from '@/api/modules/clientSettings.js';
import authService from '@/helpers/authservice/authService.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'RegistrationGiftCampaignList',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('註冊禮');
    const loading = ref(false);
    const campaigns = ref([]);
    const isWixtarClient = ref(false);
    const wixtarWebhookRegistrationGiftEnabled = ref(false);
    const searchQuery = ref('');
    const filterStatus = ref('');
    const createDialogOpen = ref(false);
    const newCampaignName = ref('新註冊禮活動');
    const createError = ref('');
    const createSubmitting = ref(false);
    const createNameInput = ref(null);

    const loadMemberSystemType = async () => {
      try {
        const user = authService.getCurrentUser();
        if (!user?.clientId) {
          isWixtarClient.value = false;
          wixtarWebhookRegistrationGiftEnabled.value = false;
          return;
        }
        const res = await clientService.getClient(user.clientId);
        isWixtarClient.value = res?.data?.clientData?.memberSystemType === 'WIXTAR';
        if (isWixtarClient.value) {
          await loadWixtarRegistrationGiftSwitch();
        } else {
          wixtarWebhookRegistrationGiftEnabled.value = false;
        }
      } catch {
        isWixtarClient.value = false;
        wixtarWebhookRegistrationGiftEnabled.value = false;
      }
    };

    const loadWixtarRegistrationGiftSwitch = async () => {
      try {
        const wixtarRes = await clientSettingsService.get(NAMESPACES.WIXTAR);
        const wixtar = wixtarRes?.data?.settings ?? {};
        wixtarWebhookRegistrationGiftEnabled.value = !!wixtar.wixtarWebhookRegistrationGiftEnabled;
      } catch (err) {
        console.error('載入 Wixtar 註冊禮開關失敗:', err);
        wixtarWebhookRegistrationGiftEnabled.value = false;
      }
    };

    const loadCampaigns = async () => {
      loading.value = true;
      try {
        const response = await autoSendService.getRegistrationGiftModules();
        campaigns.value = response?.success && Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        showError('載入活動清單失敗', error.message || '請稍後再試');
      } finally {
        loading.value = false;
      }
    };

    const filteredCampaigns = computed(() => {
      let items = campaigns.value;
      const q = searchQuery.value.trim().toLowerCase();
      if (q) {
        items = items.filter(c => (c.moduleName || '').toLowerCase().includes(q));
      }
      if (filterStatus.value) {
        items = items.filter(c => c.effectiveStatus === filterStatus.value);
      }
      return items;
    });

    const handleSearch = () => {};

    const statusLabel = (status) => ({
      disabled: '已停用',
      scheduled: '尚未生效',
      active: '生效中',
      expired: '已過期'
    }[status] || status || '-');

    const statusBadgeClass = (status) => ({
      disabled: 'badge-secondary',
      scheduled: 'badge-warning',
      active: 'badge-success',
      expired: 'badge-danger'
    }[status] || 'badge-secondary');

    const formatDate = (isoString) => {
      if (!isoString) return '-';
      return new Date(isoString).toLocaleString('zh-TW', {
        timeZone: 'Asia/Taipei',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const closeCreateDialog = () => {
      if (createSubmitting.value) return;
      createDialogOpen.value = false;
      createError.value = '';
    };

    const handleCreateCampaign = async () => {
      createError.value = '';
      newCampaignName.value = '新註冊禮活動';
      createDialogOpen.value = true;
      await nextTick();
      createNameInput.value?.focus();
      createNameInput.value?.select();
    };

    const confirmCreateCampaign = async () => {
      const moduleName = newCampaignName.value?.trim();
      if (!moduleName) {
        createError.value = '請輸入活動名稱';
        return;
      }

      createSubmitting.value = true;
      try {
        const response = await autoSendService.createRegistrationGiftModule({
          moduleName,
          copyFromModuleId: campaigns.value[0]?.id || undefined,
          isEnabled: false
        });

        if (response?.success && response.data?.id) {
          createDialogOpen.value = false;
          await showSuccess('活動建立成功', '已建立新活動，請進入編輯頁設定內容');
          router.push({ name: 'RegistrationGiftConfigEdit', params: { moduleId: response.data.id } });
          return;
        }
        createError.value = response?.message || '建立失敗';
      } catch (error) {
        createError.value = error.message || '建立失敗';
      } finally {
        createSubmitting.value = false;
      }
    };

    const handleDeleteCampaign = async (campaign) => {
      if (campaigns.value.length <= 1) {
        showError('無法刪除', '至少需保留一個活動');
        return;
      }

      const confirmed = window.confirm(`確定要刪除活動「${campaign.moduleName}」嗎？`);
      if (!confirmed) return;

      try {
        loading.value = true;
        const response = await autoSendService.deleteRegistrationGiftModule(campaign.id);
        if (response?.success) {
          await showSuccess('活動已刪除');
          await loadCampaigns();
        } else {
          showError('刪除失敗', response?.message || '未知錯誤');
        }
      } catch (error) {
        showError('刪除失敗', error.message || '請稍後再試');
      } finally {
        loading.value = false;
      }
    };

    onMounted(async () => {
      await Promise.all([loadCampaigns(), loadMemberSystemType()]);
    });

    return {
      pageTitle,
      loading,
      campaigns,
      isWixtarClient,
      wixtarWebhookRegistrationGiftEnabled,
      filteredCampaigns,
      searchQuery,
      filterStatus,
      createDialogOpen,
      newCampaignName,
      createError,
      createSubmitting,
      createNameInput,
      handleSearch,
      statusLabel,
      statusBadgeClass,
      formatDate,
      handleCreateCampaign,
      closeCreateDialog,
      confirmCreateCampaign,
      handleDeleteCampaign
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.wixtar-gift-notice {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 16px;
  padding: 12px 14px;
  background: #f0f7ff;
  border: 1px solid #cfe2ff;
  border-radius: 8px;
  color: #334155;
  font-size: 13px;
  line-height: 1.55;
}

.wixtar-gift-notice > i {
  color: #0d6efd;
  font-size: 18px;
  margin-top: 2px;
}

.wixtar-gift-notice-title {
  margin: 0 0 6px;
  font-weight: 600;
  color: #1e3a5f;
}

.wixtar-gift-notice ul {
  margin: 0;
  padding-left: 18px;
}

.wixtar-gift-notice li + li {
  margin-top: 4px;
}

.wixtar-switch-status {
  margin: 0 0 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.wixtar-switch-link {
  font-size: 13px;
}

.table-wrapper table {
  min-width: 860px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.create-lightbox-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.45);
}

.create-lightbox {
  width: 100%;
  max-width: 440px;
  padding: 22px 24px 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
}

.create-lightbox-title {
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 600;
}

.create-lightbox-text {
  margin: 0 0 16px;
  font-size: 14px;
  color: #475569;
  line-height: 1.55;
}

.create-lightbox-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.error-text {
  display: block;
  margin-top: 6px;
  color: #dc3545;
  font-size: 13px;
}
</style>
